import { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router';
import { Sheet } from '../../components/Sheet';
import { LoadingState } from '../../components/states';
import { Button } from '../../components/ui';
import { useRuntime } from '../../hooks/useRuntime';
import { ScreenWakeLock, isIos, vibrate } from '../../lib/device';
import { formatDistance, formatDuration, formatPace } from '../../lib/format';
import { useTracking } from '../../services/runtime';
import { useAuth } from '../../stores/auth';
import { useNetwork } from '../../stores/network';
import { useCurrentRegion } from '../../stores/region';
import { toast } from '../../stores/toasts';
import { GameMap } from '../map/GameMap';
import { useOwnedTerritories } from '../map/useOwnedTerritories';
import { GpsChip } from './GpsChip';
import { useT } from '../../i18n';

const wakeLock = new ScreenWakeLock();

export function RunPage() {
  const { runtime, region } = useRuntime();
  const snap = useTracking();
  const navigate = useNavigate();
  const user = useAuth((s) => s.user);
  const online = useNetwork((s) => s.online);
  const { owners } = useOwnedTerritories(region);
  const [confirmFinish, setConfirmFinish] = useState(false);
  const [busy, setBusy] = useState(false);
  const [follow, setFollow] = useState(true);
  const [, setTick] = useState(0);
  const t = useT();
  const engine = runtime?.engine;
  const state = snap.state;
  const active = state === 'RUNNING' || state === 'PAUSED' || state === 'RESUMING';

  // GPS preview while on this screen; stop it when leaving without a run.
  useEffect(() => {
    if (!engine) return;
    engine.warmUp();
    useCurrentRegion.getState().followGps();
    return () => engine.coolDown();
  }, [engine]);

  // Clock tick + wake lock while running.
  useEffect(() => {
    if (!active) return;
    const t = setInterval(() => setTick((n) => n + 1), 1000);
    void wakeLock.enable();
    return () => {
      clearInterval(t);
      void wakeLock.disable();
    };
  }, [active]);

  if (!runtime || !region || !engine) return <LoadingState label={t('Preparing GPS…')} />;

  async function start() {
    if (!user) return;
    if (snap.gpsStatus === 'denied') {
      toast({ kind: 'error', title: t('Location blocked'), body: t('Enable location for this site to run.') });
      return;
    }
    setBusy(true);
    try {
      await engine!.start(user.id);
      vibrate(80);
      void runtime!.sync.kick();
    } catch (e) {
      toast({ kind: 'error', title: t('Could not start'), body: (e as Error).message });
    } finally {
      setBusy(false);
    }
  }

  async function finish() {
    setBusy(true);
    try {
      const run = await engine!.finish();
      vibrate([100, 50, 100]);
      engine!.reset();
      setConfirmFinish(false);
      void runtime!.sync.kick();
      navigate(`/runs/${run.id}`, { replace: true, state: { fresh: true } });
    } catch (e) {
      toast({ kind: 'error', title: t('Could not finish'), body: (e as Error).message });
    } finally {
      setBusy(false);
    }
  }

  async function discard() {
    setBusy(true);
    await engine!.discard();
    setConfirmFinish(false);
    setBusy(false);
  }

  const activeMs = engine.activeMsAt();
  const dist = formatDistance(snap.distanceM);
  const pace = snap.distanceM > 10 ? activeMs / 1000 / (snap.distanceM / 1000) : null;
  const cellOwner = snap.currentCell ? owners.get(snap.currentCell)?.owner : null;
  const cellState = !snap.currentCell
    ? null
    : !region.isPlayable(snap.currentCell)
      ? 'outside'
      : snap.capturedCells.includes(snap.currentCell) || cellOwner?.id === user?.id
        ? 'yours'
        : cellOwner
          ? 'enemy'
          : 'neutral';

  return (
    <div className="fixed inset-0 z-[35] flex flex-col bg-ink-900" data-testid="run-screen" data-state={state}>
      <div className="relative min-h-0 flex-1">
        <GameMap
          key={region.dto.id}
          className="absolute inset-0"
          region={region}
          owners={owners}
          myUserId={user?.id ?? null}
          position={snap.position}
          follow={follow}
          route={snap.route}
          routeVersion={snap.routeVersion}
          highlight={snap.capturedCells}
          initialZoom={16}
          controls={false}
        />
        <div className="absolute inset-x-0 top-0 flex items-start justify-between gap-2 p-3 pt-[calc(var(--safe-top)+12px)]">
          <GpsChip status={snap.gpsStatus} accuracy={snap.position?.accuracy} />
          <div className="flex flex-col items-end gap-1.5">
            {!online ? (
              <span className="rounded-full bg-warn px-3 py-1 text-xs font-bold uppercase text-ink-950" data-testid="run-offline">
                {t('Offline · saved locally')}
              </span>
            ) : null}
            {!active ? (
              <button
                onClick={() => navigate(-1)}
                className="rounded-full bg-ink-850/90 px-3 py-1 text-xs font-bold uppercase text-ink-300"
              >
                {t('Close')}
              </button>
            ) : (
              <button
                onClick={() => setFollow((f) => !f)}
                className={`rounded-full px-3 py-1 text-xs font-bold uppercase ${follow ? 'bg-volt text-ink-950' : 'bg-ink-850/90 text-ink-300'}`}
              >
                {t(follow ? 'Following' : 'Follow me')}
              </button>
            )}
          </div>
        </div>
        {snap.storageError || !snap.storageDurable ? (
          <div className="absolute inset-x-3 bottom-3 rounded-xl bg-enemy px-3 py-2 text-xs font-semibold text-white" role="alert">
            {snap.storageError ?? t('This browser cannot store data durably — keep the app open until the run is synced.')}
          </div>
        ) : null}
      </div>

      <section className="rounded-t-3xl border-t border-ink-700 bg-ink-900 px-5 pb-[calc(var(--safe-bottom)+16px)] pt-4">
        {active || state === 'FINISHING' ? (
          <>
            <div className="text-center">
              <div className="font-display text-7xl font-extrabold leading-none tabular" data-testid="run-distance">
                {dist.value}
                <span className="ml-1 text-2xl font-bold text-ink-400">{dist.unit}</span>
              </div>
            </div>
            <div className="mt-3 grid grid-cols-3 gap-2 text-center">
              <Metric label={t('Time')} value={formatDuration(activeMs / 1000)} testId="run-time" />
              <Metric label={t('Pace now')} value={formatPace(snap.currentPaceSecPerKm)} accent testId="run-current-pace" />
              <Metric label={t('Avg pace')} value={formatPace(pace)} testId="run-pace" />
            </div>
            <div className="mt-3 flex flex-wrap items-center justify-center gap-2 text-xs">
              <span className="rounded-full bg-volt/15 px-3 py-1 font-bold uppercase tracking-wider text-volt">
                ⬢ <span data-testid="run-captured">{snap.capturedCells.length}</span> {t('captured')}
              </span>
              {cellState ? <SectorChip gridKey={snap.currentCell!} state={cellState} meters={snap.currentCellMeters} owner={cellOwner?.displayName} /> : null}
            </div>
            {snap.splits.length ? (
              <p className="mt-2 text-center text-xs text-ink-400" data-testid="last-split">
                {t('Km')} {snap.splits[snap.splits.length - 1]!.index} · {formatPace(snap.splits[snap.splits.length - 1]!.paceSecPerKm)}/km
              </p>
            ) : null}
            {state === 'PAUSED' ? (
              <p className="mt-2 text-center text-sm font-semibold uppercase tracking-widest text-gold" data-testid="paused-label">
                {t('Paused')}
              </p>
            ) : null}
            <div className="mt-4 grid grid-cols-2 gap-3">
              {state === 'PAUSED' ? (
                <Button size="lg" onClick={() => engine.resume()} data-testid="resume-run">
                  {t('Resume')}
                </Button>
              ) : (
                <Button size="lg" variant="secondary" onClick={() => engine.pause()} disabled={state === 'FINISHING'} data-testid="pause-run">
                  {t('Pause')}
                </Button>
              )}
              <Button size="lg" variant="danger" onClick={() => setConfirmFinish(true)} disabled={state === 'FINISHING'} data-testid="finish-run">
                {t('Finish')}
              </Button>
            </div>
          </>
        ) : (
          <PreRun
            gpsStatus={snap.gpsStatus}
            hasFix={!!snap.position}
            inRegion={snap.inRegion}
            regionName={region.dto.name}
            busy={busy || state === 'STARTING'}
            onStart={start}
          />
        )}
      </section>

      <Sheet open={confirmFinish} onClose={() => setConfirmFinish(false)} label={t('Finish run')}>
        <h3 className="font-display text-2xl font-bold uppercase">{t('Finish this run?')}</h3>
        <p className="mt-1 text-sm text-ink-400">
          {dist.value} {dist.unit} · {formatDuration(activeMs / 1000)} · {t('{n} sector(s) pending.', { n: snap.capturedCells.length })}{' '}
          {t(online ? 'The server will validate it now.' : 'You are offline — it will be validated when you reconnect.')}
        </p>
        <div className="mt-5 grid gap-3">
          <Button size="lg" onClick={finish} loading={busy} data-testid="confirm-finish">
            {t('Finish & save')}
          </Button>
          <Button size="lg" variant="secondary" onClick={() => setConfirmFinish(false)}>
            {t('Keep running')}
          </Button>
          {snap.distanceM < 50 ? (
            <Button size="md" variant="ghost" onClick={discard} data-testid="discard-run">
              {t('Discard run')}
            </Button>
          ) : null}
        </div>
      </Sheet>
    </div>
  );
}

function PreRun({
  gpsStatus,
  hasFix,
  inRegion,
  regionName,
  busy,
  onStart,
}: {
  gpsStatus: string;
  hasFix: boolean;
  inRegion: boolean;
  regionName: string;
  busy: boolean;
  onStart: () => void;
}) {
  const t = useT();
  if (gpsStatus === 'denied') {
    return (
      <div className="py-2 text-center" data-testid="gps-denied">
        <div className="font-display text-2xl font-bold uppercase">{t('Location is blocked')}</div>
        <p className="mt-2 text-sm text-ink-400">
          {t('Runner Wars needs your location to track runs.')}{' '}
          {t(isIos() ? 'Open Settings → Safari → Location → Allow, then reload.' : 'Tap the lock icon in the address bar → Permissions → Location → Allow, then reload.')}
        </p>
        <Link to="/guide#vi-tri" className="mt-3 inline-block text-sm font-semibold text-volt" data-testid="location-guide-link">
          {t('How to allow location')} ›
        </Link>
        <Button className="mt-4 w-full" variant="secondary" onClick={() => location.reload()}>
          {t('Reload')}
        </Button>
      </div>
    );
  }
  if (gpsStatus === 'unavailable') {
    return (
      <div className="py-2 text-center">
        <div className="font-display text-2xl font-bold uppercase">{t('GPS unavailable')}</div>
        <p className="mt-2 text-sm text-ink-400">{t('Turn on location services and move outside with a clear view of the sky.')}</p>
        <Button className="mt-4 w-full" onClick={onStart} loading={busy}>
          {t('Start anyway')}
        </Button>
      </div>
    );
  }
  return (
    <div>
      {hasFix && !inRegion ? (
        <p className="mb-3 rounded-xl bg-gold/10 px-3 py-2 text-center text-sm text-gold">
          {t('You’re outside {region}. A run mostly outside the game area doesn’t count.', { region: regionName })}
        </p>
      ) : null}
      {gpsStatus === 'poor' ? (
        <p className="mb-3 text-center text-sm text-ink-400">{t('Weak GPS. Move to an open area for accurate tracking.')}</p>
      ) : null}
      <Button size="xl" className="w-full" onClick={onStart} loading={busy} data-testid="start-run">
        {t(hasFix ? 'Start' : 'Start (waiting for GPS)')}
      </Button>
      <p className="mt-3 text-center text-xs text-ink-500">
        {t('Keep this screen open while running.')} {isIos() ? t('iOS pauses GPS when the screen is locked.') : ''}
      </p>
    </div>
  );
}

function Metric({ label, value, accent, testId }: { label: string; value: string; accent?: boolean; testId?: string }) {
  return (
    <div className="rounded-2xl bg-ink-850 py-2">
      <div className={`font-display text-3xl font-bold tabular ${accent ? 'text-volt' : ''}`} data-testid={testId}>
        {value}
      </div>
      <div className="text-[11px] font-semibold uppercase tracking-widest text-ink-400">{label}</div>
    </div>
  );
}

function SectorChip({ gridKey, state, meters, owner }: { gridKey: string; state: string; meters: number; owner?: string }) {
  const t = useT();
  const cls =
    state === 'yours'
      ? 'bg-volt/15 text-volt'
      : state === 'enemy'
        ? 'bg-enemy/15 text-enemy'
        : state === 'outside'
          ? 'bg-ink-700 text-ink-400'
          : 'bg-ink-700 text-ink-200';
  const text =
    state === 'yours'
      ? t('Yours')
      : state === 'enemy'
        ? `${owner ?? t('Enemy')} · ${Math.min(meters, 30)}/30 m`
        : state === 'outside'
          ? t('Outside game area')
          : `${t('Neutral')} · ${Math.min(meters, 30)}/30 m`;
  return (
    <span className={`rounded-full px-3 py-1 font-bold uppercase tracking-wider ${cls}`} data-testid="sector-chip">
      ⬡ {gridKey} · {text}
    </span>
  );
}
